import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { clickstreamTracker } from '@pukka/clickstream';
import { Loader, PageContainer, Text } from '@pukka/ui-components';
import { DefaultActionBar, BillingDiscountReminder } from '@pukka/ui-composite';
import { useFlagWithFallback } from '@pukka/unleash';
import { useSnapshot } from 'valtio';

import { BillingPersistedState, BillingState } from './Billing.state';
import { BillingActiveOutlet } from './BillingActiveOutlet';
import { BillingCurrentPlan } from './BillingCurrentPlan';
import { BillingHistory } from './BillingHistory';
import { BillingPurchasedSlots } from './BillingPurchasedSlots';
import { BillingReceipt, BillingProcessing } from './BillingReceipt';
import { PaymentState } from './BillingReceipt/BillingReceipt.state';

interface BillingPageProps {
  readonly persistedState?: typeof BillingPersistedState;
  readonly state?: typeof BillingState;
}

function BillingPage({ persistedState = BillingPersistedState, state = BillingState }: BillingPageProps) {
  const [enableRecurringPayment] = useFlagWithFallback('recurring-payment-backoffice');
  const { isRecurring, profile, regular, histories } = useSnapshot(persistedState);
  const {
    status: { regular: status },
    isPayingRecurring,
    paymentStatus,
  } = useSnapshot(state);
  const { useBillingData } = state;

  useBillingData();

  const { isEnterprise } = useSnapshot(PaymentState);

  useEffect(() => {
    clickstreamTracker.trackPage('MOKAPOS_SETTING_BILLING_START');
  }, []);

  if (enableRecurringPayment && isRecurring) {
    return <Navigate to="recurring" />;
  }

  return (
    <PageContainer className="w-full">
      {paymentStatus === 'processing' && (
        <div className="fixed left-0 top-0 z-[100] h-screen w-screen bg-neutral-900 opacity-80">
          <div className="fixed inset-0 m-auto h-fit w-fit text-center">
            <Loader size={200} className="m-auto stroke-blue-100" />
            <br />
            <Text color="white" variant="heading-1">
              We&apos;re processing your payment. This might take up to 2 minutes
            </Text>
            <Text color="white">Please wait and don&apos;t close this tab</Text>
          </div>
        </div>
      )}
      <DefaultActionBar pageTitle="Billing" />
      <div className="flex flex-col gap-5 md:gap-10">
        <BillingDiscountReminder show={profile?.isOwner} businessId={profile?.businessId} expiredAt={profile?.expiredAt} />
        <div className="flex flex-col gap-5 md:flex-row">
          <div className="flex flex-1 flex-col gap-8">
            <BillingCurrentPlan loading={status === 'loading'} currentPlanName={regular?.plan} lastPaymentDate={regular?.lastPaymentDate} />
            {regular?.employeeSlotCount ? <BillingPurchasedSlots count={regular?.employeeSlotCount} /> : null}
            <BillingActiveOutlet outlets={regular?.outlets} />
          </div>
          {isPayingRecurring ? (
            <BillingProcessing className="view-transition-[billing-receipt]" />
          ) : (
            <BillingReceipt className="view-transition-[billing-receipt]" />
          )}
        </div>
        {!isEnterprise && <BillingHistory data={histories} />}
      </div>
    </PageContainer>
  );
}

export { BillingPage };
